class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    // Only intercept the keys we actually navigate with. The blanket
    // preventDefault() used to swallow the default behaviour of every keyup
    // (including Enter's form submit) which blocked the full-search fallback.
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        // Enter selects the highlighted suggestion when there is one; otherwise
        // it falls through to the native form submit for the full results page.
        if (this.querySelector('[aria-selected="true"]')) {
          event.preventDefault();
          this.selectOption();
        }
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;

    // Count occurrences with plain string operations (no RegExp) so a term
    // containing regex metacharacters can never throw and break the keystroke.
    // When the old term appears more than once it is part of other copy, so we
    // leave the label alone rather than mangling it.
    const occurrences = currentButtonText.split(previousTerm).length - 1;
    if (occurrences > 1) return;

    searchForTextElement.innerText = currentButtonText.split(previousTerm).join(newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Deterministic, non-user-exposed failure hook: an automation or a local
    // source test can set `data-force-error-term="<term>"` on this element to
    // exercise the inline error state without a real network failure. Real
    // storefronts never set the attribute, so shoppers are unaffected.
    if (this.shouldForceError(searchTerm)) {
      // Drop any cached markup for the term first so a previously successful
      // query can never mask the failure we are deliberately reproducing.
      this.clearCachedResult(queryKey);
      this.renderErrorState();
      return;
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          // Render an inline error state instead of closing the dropdown and
          // throwing an uncaught rejection (which surfaced as a console error
          // and left the shopper with no feedback).
          this.renderErrorState();
          return null;
        }

        return response.text();
      })
      .then((text) => {
        if (text == null) return;

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search')?.innerHTML;

        if (typeof resultsMarkup !== 'string') {
          this.renderErrorState();
          return;
        }

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // Code 20 / AbortError means the call was superseded by a newer
        // keystroke — silently discard so the in-flight request wins.
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        this.renderErrorState();
      });
  }

  // Inline failure state. Keeps the AbortController intact for the next
  // keystroke, announces the message to assistive tech, and lets the shopper
  // press Enter to fall back to the full search results page.
  renderErrorState() {
    const errorText = this.getAttribute('data-error-text') || '';

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';

      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search__error-state';
      wrapper.setAttribute('role', 'presentation');

      const message = document.createElement('p');
      message.className = 'predictive-search__error-text';
      message.textContent = errorText;
      wrapper.appendChild(message);

      this.predictiveSearchResults.appendChild(wrapper);
    }

    this.removeAttribute('loading');
    this.setAttribute('results', true);
    this.setLiveRegionText(errorText);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);

    // Reveal the dropdown while loading so the spinner reserves the results
    // area up front — the header no longer grows/shrinks between keystrokes.
    this.open();
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || statusText == null) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // Reads the deterministic forced-failure hook off the element. Empty in
  // production so the branch is inert for shoppers. Automation (or a local
  // source test) can set `data-force-error-term="<term>"` on this element to
  // exercise the inline error state for that exact term, or `"*"` to force it
  // for every non-empty term without touching the network layer.
  shouldForceError(searchTerm) {
    const forcedTerm = (this.getAttribute('data-force-error-term') || '').trim();
    const term = String(searchTerm).trim();
    return forcedTerm.length > 0 && term.length > 0 && (forcedTerm === '*' || forcedTerm === term);
  }

  // Removes a term from every predictive-search instance's cache so a forced
  // retry can never be served stale markup.
  clearCachedResult(queryKey) {
    this.allPredictiveSearchInstances.forEach((instance) => {
      delete instance.cachedResults[queryKey];
    });
  }

  // Normalises copy for the low-confidence comparison: lower-case and collapse
  // anything that is not a letter or number to a single space.
  normalizeForMatch(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  // Every rendered result row (queries, collections, pages, articles and
  // products), flattened into word tokens. Deliberately scoped to
  // `.predictive-search__results-list` so the always-present "Search for …"
  // fallback button can never contribute the typed term to the comparison —
  // including it made every term look like a match and permanently suppressed
  // the empty state. Only product rows contribute their link handle (the one
  // place the href is a real identity signal rather than an echo of the query).
  collectResultWords() {
    const words = [];
    this.querySelectorAll('.predictive-search__results-list .predictive-search__item').forEach((element) => {
      const parts = [element.textContent || ''];
      if (element.classList.contains('predictive-search__item--link-with-thumbnail')) {
        parts.push(element.getAttribute('href') || '');
      }
      const normalized = this.normalizeForMatch(parts.join(' '));
      if (normalized) words.push(...normalized.split(' '));
    });
    return words;
  }

  // A result is plausible when any term word shares a 3-character prefix with a
  // result word in either direction, which keeps genuine fuzzy/prefix matches
  // ("dres" -> "Dress", "kimonos" -> "Kimono") while rejecting unrelated
  // broad-fallback products.
  hasPlausibleMatch(searchTerm) {
    const tokens = this.normalizeForMatch(searchTerm)
      .split(' ')
      .filter((token) => token.length >= 3);
    if (!tokens.length) return true;

    const words = this.collectResultWords();
    if (!words.length) return true;

    return tokens.some((token) =>
      words.some((word) => {
        const prefixLength = Math.min(3, token.length, word.length);
        return prefixLength >= 3 && token.slice(0, prefixLength) === word.slice(0, prefixLength);
      })
    );
  }

  // Low-confidence guard: only replace grouped results with the designed empty
  // state when the term is long enough to be a deliberate no-match probe and
  // none of the returned rows share any prefix with it.
  shouldShowEmptyState(searchTerm) {
    if (this.normalizeForMatch(searchTerm).replace(/ /g, '').length < 4) return false;

    // The server already rendered the designed empty state for a true zero.
    if (this.querySelector('.predictive-search__empty-state')) return false;

    // Only act when Shopify actually returned grouped results to judge.
    if (!this.querySelector('#predictive-search-results-groups-wrapper')) return false;

    return !this.hasPlausibleMatch(searchTerm);
  }

  getEmptyStateCopy(searchTerm) {
    const heading = this.getAttribute('data-empty-heading') || '';
    const template = this.getAttribute('data-empty-text-template') || '';
    const text = template.split('__TERMS__').join(searchTerm);
    return { heading, text };
  }

  buildEmptyState(searchTerm) {
    const { heading, text } = this.getEmptyStateCopy(searchTerm);

    const wrapper = document.createElement('div');
    wrapper.className = 'predictive-search__empty-state';
    wrapper.setAttribute('role', 'presentation');

    if (heading) {
      const headingElement = document.createElement('p');
      headingElement.className = 'predictive-search__empty-heading h5';
      headingElement.textContent = heading;
      wrapper.appendChild(headingElement);
    }

    if (text) {
      const textElement = document.createElement('p');
      textElement.className = 'predictive-search__empty-text';
      textElement.textContent = text;
      wrapper.appendChild(textElement);
    }

    return wrapper;
  }

  // Removes the broad-fallback result groups and shows the empty panel while
  // keeping the "Search for …" full-search fallback button so the shopper still
  // has a next step. Copy is inserted as text nodes, never innerHTML.
  renderEmptyState(searchTerm) {
    this.querySelector('#predictive-search-results-groups-wrapper')?.remove();

    if (!this.querySelector('.predictive-search__empty-state')) {
      const emptyState = this.buildEmptyState(searchTerm);
      const searchForButton = this.querySelector('.predictive-search__search-for-button');
      if (searchForButton && searchForButton.parentNode) {
        searchForButton.parentNode.insertBefore(emptyState, searchForButton);
      } else {
        (this.querySelector('#predictive-search-results') || this.predictiveSearchResults)?.appendChild(emptyState);
      }
    }

    this.removeAttribute('loading');
    this.setLiveRegionText(this.getEmptyStateCopy(searchTerm).text);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    const searchTerm = this.searchTerm || this.getQuery();

    if (this.shouldShowEmptyState(searchTerm)) {
      this.renderEmptyState(searchTerm);
    } else {
      this.setLiveRegionResults();
    }

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countValue = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
    if (countValue) this.setLiveRegionText(countValue);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
